import { Component, createRef, memo } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  Circle,
  Group,
  Layer,
  Line,
  Rect,
  Shape,
  Stage,
  Text,
} from "react-konva";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import nipplejs from "nipplejs";

import "../../scss/components/_field.scss";

class FieldCanvas extends Component {
  constructor(props) {
    super(props);

    this.state = {
      points: [],
      active: null,
      stick: {
        x: 0,
        y: 0,
      },
    };

    this.defaultWidth = 1030;
    this.defaultRadius = 8;
    this.defaultStickForce = 0.05;

    this.nippleOptions = {
      color: "#ffffff",
      size: 100,
      mode: "dynamic",
    };

    this.defaultFieldPoints = [
      {
        id: "0",
        x: 0.1,
        y: 0.25,
        color: "#6200EE",
        desc: "Top Left",
      }, // Top Left
      {
        id: "1",
        x: 0.5,
        y: 0.25,
        color: "#03DAc6",
        desc: "Top Middle",
      }, // Top Middle
      {
        id: "2",
        x: 0.9,
        y: 0.25,
        color: "#B00020",
        desc: "Top Right",
      }, // Top Right
      {
        id: "3",
        x: 0.9,
        y: 0.75,
        color: "#FFDE03",
        desc: "Bottom Right",
      }, // Bottom Right
      {
        id: "4",
        x: 0.5,
        y: 0.75,
        color: "#61D800",
        desc: "Bottom Middle",
      }, // Bottom Middle
      {
        id: "5",
        x: 0.1,
        y: 0.75,
        color: "#90CAF9",
        desc: "Bottom Left",
      }, // Bottom Left
      {
        id: "6",
        x: 0.1,
        y: 0.5,
        color: "#90CAF9",
        desc: "Middle Left",
      }, // Middle Left
      {
        id: "7",
        x: 0.5,
        y: 0.5,
        color: "#90CAF9",
        desc: "Middle",
      }, // Middle Middle
      {
        id: "8",
        x: 0.9,
        y: 0.5,
        color: "#90CAF9",
        desc: "Middle Right",
      }, // Middle Right
    ];

    this.stage = createRef();
    this.container = createRef();
    this.nipple = createRef();

    this.stickListener = this.stickListener.bind(this);
    this.movePoint = this.movePoint.bind(this);

    this.changeActive = this.changeActive.bind(this);
    this.resetPoints = this.resetPoints.bind(this);

    this.canvasRender = this.canvasRender.bind(this);
    this.handleCanvasPointerDown = this.handleCanvasPointerDown.bind(this);
    this.handleCanvasKeyPress = this.handleCanvasKeyPress.bind(this);

    this.generatePoints = this.generatePoints.bind(this);
    this.generateFieldPoints = this.generateFieldPoints.bind(this);

    this.handlePointClick = this.handlePointClick.bind(this);
    this.handlePointDragStart = this.handlePointDragStart.bind(this);
    this.handlePointDragMove = this.handlePointDragMove.bind(this);
    this.handlePointDragEnd = this.handlePointDragEnd.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.generateCustomShape = this.generateCustomShape.bind(this);

    this.handleSwitchMarkupX = this.handleSwitchMarkupX.bind(this);
    this.handleSwitchMarkupY = this.handleSwitchMarkupY.bind(this);
  }

  movePoint() {
    const points = [...this.state.points];
    const pointIndex = points.findIndex(
      (point) => point.id === this.state.active
    );
    const point = points[pointIndex];
    let { x, y } = point;
    x = +(x + this.state.stick.x);
    y = +(y + this.state.stick.y);

    point.x = x;
    point.y = y;
    points[pointIndex] = point;

    this.setState((state) => ({ ...state, points: [...points] }));
  }

  changeActive(active) {
    this.setState((state) => ({ ...state, active }));
  }

  resetPoints(points) {
    let targetPoints;
    if (points && points.length > 0) {
      targetPoints = this.restoreFieldPoints(points);
    } else {
      targetPoints = this.generateFieldPoints();
    }
    this.setState((state) => ({
      ...state,
      points: targetPoints,
      active: null,
    }));
  }

  canvasRender() {
    /* Update Stage width & height according to parent container. */
    const stage = this.stage.current;
    const container = this.container.current;
    if (stage) {
      stage.width(container.offsetWidth);
      stage.height(container.offsetHeight);
    }
  }

  componentDidMount() {
    if (this.state.points.length === 0) {
      /* Generate default field & zone points if there is not saved TrackMatching. */
      const points = this.generateFieldPoints();
      this.setState((state) => ({ ...state, points }));
    }
    document.addEventListener("keyup", this.handleCanvasKeyPress);
    window.addEventListener("resize", this.handleWindowResize);
    this.manager = nipplejs.create({
      ...this.nippleOptions,
      zone: this.nipple.current,
    });
    this.stickListener(this.manager);
    this.canvasRender();
  }

  componentDidUpdate() {
    this.canvasRender();
    this.props.onUpdate();
  }

  componentWillUnmount() {
    document.removeEventListener("keyup", this.handleCanvasKeyPress);
    window.removeEventListener("resize", this.handleWindowResize);
  }

  generatePoints(data) {
    return data.map((point) => ({
      id: point.id,
      x: point.x,
      y: point.y,
      color: point.color,
      desc: point.desc,
      isDraggable: false,
      ref: createRef(),
    }));
  }

  generateFieldPoints() {
    /* Generate default football field TrackMatching with 6 points: left, middle, right for top and bottom lines. */
    return this.generatePoints(this.defaultFieldPoints);
  }

  restoreFieldPoints(points) {
    const targetPoints = [];
    for (let index = 0; index < points.length; index += 1) {
      const source = points[index];
      const target = this.defaultFieldPoints.find(
        (point) => point.id === source.id
      );
      targetPoints.push({ ...target, ...source });
    }
    return targetPoints;
  }

  handlePointDragStart(e) {
    const { target } = e;
    const id = target.id();

    const points = [
      ...this.state.points.map((point) => ({
        ...point,
        isDragging: point.id === id,
      })),
    ];
    this.setState((state) => ({ ...state, points, active: id }));
  }

  generateCustomShape(context, shape) {
    const container = this.container.current;

    context.beginPath();
    let { points } = this.state;
    context.moveTo(
      points[0].x * container.offsetWidth,
      points[0].y * container.offsetHeight
    );
    if (points.length === 9) {
      points = [
        points[0],
        points[1],
        points[2],
        points[8],
        ...points.slice(3, 7),
      ];
    }
    for (let i = 0; i < points.length; i++) {
      context.lineTo(
        points[i].x * container.offsetWidth,
        points[i].y * container.offsetHeight
      );
    }
    context.closePath();
    // (!) Konva specific method, it is very important
    context.fillStrokeShape(shape);
  }

  handlePointDragMove(e) {
    /* Update component state in order to rerender the canvas. */
    const container = this.container.current;

    const { target } = e;
    const id = target.id();
    const pointIndex = this.state.points.findIndex((point) => point.id === id);
    const points = [...this.state.points];
    const point = points[pointIndex];

    e.target.x(0);
    e.target.y(0);

    let { x, y } = target.getRelativePointerPosition();
    const _x = +(x / container.offsetWidth);
    const _y = +(y / container.offsetHeight);

    point.x = _x;
    point.y = _y;
    points[pointIndex] = point;

    this.setState((state) => ({ ...state, points: [...points] }));
  }

  handlePointDragEnd(e) {
    const id = e.target.id();
    const point = this.state.points.find((point) => point.id === id);
    point.isDragging = false;
  }

  handlePointClick(e) {
    const id = e.target.id();
    this.setState((state) => ({ ...state, active: id }));
  }

  handleCanvasPointerDown(e) {
    if (e.target !== this.nipple.current) {
      const stage = this.stage.current;
      const targetRect = e.target.getBoundingClientRect();
      const pos = {
        x: e.pageX - targetRect.left - window.scrollX,
        y: e.pageY - targetRect.top - window.scrollY,
      };
      const shape = stage.getIntersection(pos);
      if (!shape.attrs.hasOwnProperty("id") && this.state.active) {
        const container = this.nipple.current;
        container.dispatchEvent(new PointerEvent("pointerdown", { ...e }));
      }
    }
  }

  handleCanvasKeyPress(e) {
    switch (e.key) {
      case "Escape":
        this.setState((state) => ({ ...state, active: null }));
        break;
      // case 'Delete':
      //     if (this.state.points.length > 4) {
      //         const {active} = this.state
      //         if (active) {
      //             const index = this.state.points.findIndex((point) => point.id === active)
      //             const points = [...this.state.points]
      //             points.splice(index, 1)
      //             this.setState((state) => ({...state, points, active: null}))
      //         }
      //     }
      //     break
      default:
        break;
    }
  }

  handleWindowResize() {
    /* Force canvas to render if the viewport has been resized. */
    this.canvasRender();
    this.setState((state) => ({ ...state }));
  }

  stickListener(manager) {
    let interval;
    manager.on("added", () => {
      interval = setInterval(() => {
        this.movePoint();
      }, 25);
    });
    manager.on("move", (e, stick) => {
      const container = this.container.current;
      const offsetY = +(
        ((stick.distance * Math.sin(stick.angle.radian)) /
          container.offsetHeight) *
        this.defaultStickForce *
        -1
      );
      const offsetX = +(
        ((stick.distance * Math.cos(stick.angle.radian)) /
          container.offsetWidth) *
        this.defaultStickForce
      );

      this.setState((state) => ({
        ...state,
        stick: { x: offsetX, y: offsetY },
      }));
    });
    manager.on("end", () => {
      clearInterval(interval);
      this.setState((state) => ({ ...state, stick: { x: 0, y: 0 } }));
    });
  }

  handleSwitchMarkupX() {
    const points = this.state.points.map((point) => {
      const newHorizontalPosition = +(1 - point.x).toFixed(2);
      return { ...point, x: newHorizontalPosition };
    });

    this.setState({ ...this.state, points });
  }

  handleSwitchMarkupY() {
    const points = this.state.points.map((point) => {
      const newVerticalPosition = +(1 - point.y).toFixed(2);
      return { ...point, y: newVerticalPosition };
    });

    this.setState({ ...this.state, points });
  }

  render() {
    const container = this.container.current;
    const width = container ? container.offsetWidth : 0;
    const height = container ? container.offsetHeight : 0;
    const actualRadius = container
      ? (width / this.defaultWidth) * this.defaultRadius
      : this.defaultRadius;

    const activePoint =
      this.state.active &&
      this.state.points.find((point) => point.id === this.state.active);

    const toAbsX = (x) => x * width;
    const toAbsY = (y) => y * height;

    const generateShapePoints = (points) => {
      return points.flatMap((point, i, arr) => {
        const points = [toAbsX(point.x), toAbsY(point.y)];
        if (i === arr.length - 1) {
          points.push(toAbsX(arr[0].x), toAbsY(arr[0].y));
        }
        return points;
      });
    };

    const shapePoints =
      container &&
      (this.state.points.length !== 9
        ? generateShapePoints(this.state.points)
        : generateShapePoints([
            this.state.points[7],
            this.state.points[1],
            this.state.points[0],
            this.state.points[6],
            this.state.points[7],
            this.state.points[6],
            this.state.points[5],
            this.state.points[4],
            this.state.points[7],
            this.state.points[4],
            this.state.points[3],
            this.state.points[8],
            this.state.points[7],
            this.state.points[8],
            this.state.points[2],
            this.state.points[1],
            this.state.points[7],
          ]));
    const canvasActivated = this.props.state.firstStageEditor;
    return (
      <>
        <div
          id="canvas-container"
          ref={this.container}
          onPointerDown={this.handleCanvasPointerDown}
          data-mode={canvasActivated ? "true" : "false"}
        >
          <div
            id="nipple-container"
            ref={this.nipple}
            style={this.nippleOptions}
          />
          <Stage
            ref={this.stage}
            width={1}
            height={1}
            opacity={canvasActivated ? 1 : 0.35}
          >
            {container && (
              <Layer>
                <Rect
                  width={width}
                  height={height}
                  fill="#000000"
                  opacity={0.15}
                />
                <Line points={shapePoints} stroke="white" tensions={1} />
                <Shape
                  sceneFunc={this.generateCustomShape}
                  fill="#00D2FF"
                  opacity={0.25}
                />
                {this.state.points.map((point) => (
                  /* Render points groups. Each group contains a circle, which represents a point,
                        and a text label with short point description. */
                  <Group
                    ref={point.ref}
                    key={`group.${point.id}`}
                    id={point.id}
                    draggable
                    onDragStart={this.handlePointDragStart}
                    onDragMove={this.handlePointDragMove}
                    onDragEnd={this.handlePointDragEnd}
                    onClick={this.handlePointClick}
                  >
                    <Circle
                      key={`group.${point.id}.circle`}
                      id={point.id}
                      x={toAbsX(point.x)}
                      y={toAbsY(point.y)}
                      radius={actualRadius / 3}
                      fill={
                        this.state.active === point.id ? "white" : point.color
                      }
                      shadowColor="white"
                      shadowOffset={{ x: 1, y: 1 }}
                      shadowBlur={10}
                      shadowOpacity={this.state.active === point.id ? 1 : 0}
                    />
                    <Text
                      key={`group.${point.id}.text`}
                      id={`group.${point.id}.text`}
                      x={toAbsX(point.x) - actualRadius * 2 - 15}
                      y={toAbsY(point.y) + actualRadius * 2}
                      fill="#ffffff"
                      text={`${point.desc}`}
                      opacity={canvasActivated ? 1.0 : 0.0}
                    />
                  </Group>
                ))}
                {activePoint && (
                  <Text
                    x={toAbsX(0.01)}
                    y={toAbsY(0.025)}
                    text={`Active point: ${activePoint.desc}`}
                    fill="white"
                    fontSize={14}
                  />
                )}
              </Layer>
            )}
          </Stage>
        </div>
        <OverlayTrigger
          placement="top"
          delay={{ hide: 200 }}
          overlay={(props) => (
            <Tooltip id="field-markup-switch-tooltip" {...props}>
              Change orientation
            </Tooltip>
          )}
        >
          <Button
            className="field-markup-switch-y"
            variant="primary"
            size="sm"
            onClick={this.handleSwitchMarkupY}
          >
            <FontAwesomeIcon icon={["fas", "exchange-alt"]} />
          </Button>
        </OverlayTrigger>
        <OverlayTrigger
          placement="top"
          delay={{ hide: 200 }}
          overlay={(props) => (
            <Tooltip id="field-markup-switch-tooltip" {...props}>
              Change orientation
            </Tooltip>
          )}
        >
          <Button
            className="field-markup-switch-x"
            variant="success"
            size="sm"
            onClick={this.handleSwitchMarkupX}
          >
            <FontAwesomeIcon icon={["fas", "exchange-alt"]} />
          </Button>
        </OverlayTrigger>
      </>
    );
  }
}

export default memo(FieldCanvas);
